import { FormEvent, useEffect, useMemo, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { MeetingSchedulerEventTemplate } from "../../types/Types";
import { formatYesterdayForDateInput } from "../../utils/formatDateForInput";

/** Seats = 0 means unlimited capacity on create/register. */
const UNLIMITED_SEATS = 0;

type Props = {
  show: boolean;
  membershipCategory: string;
  initialJson?: string | null;
  saving?: boolean;
  onHide: () => void;
  onSave: (template: MeetingSchedulerEventTemplate) => void;
};

function parseTemplate(json?: string | null): MeetingSchedulerEventTemplate | null {
  if (!json?.trim()) return null;
  try {
    return JSON.parse(json) as MeetingSchedulerEventTemplate;
  } catch {
    return null;
  }
}

export default function MeetingSchedulerEventConfigModal({
  show,
  membershipCategory,
  initialJson,
  saving = false,
  onHide,
  onSave,
}: Props) {
  const existing = useMemo(() => parseTemplate(initialJson), [initialJson]);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!show) return;
    const t = existing;
    setName(t?.name || `${membershipCategory} Forum`);
    setDescription(
      t?.description || `Online forum meeting for ${membershipCategory}.`,
    );
    setEmailSubject(t?.bookingConfirmationEmailSubject || "");
    setEmailBody(t?.bookingConfirmationEmailBodyHtml || "");
    setFormError("");
  }, [show, existing, membershipCategory]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setFormError("");

    const trimmedName = name.trim();
    const trimmedDescription = description.trim();
    if (!trimmedName) {
      setFormError("Event name is required.");
      return;
    }
    if (!trimmedDescription) {
      setFormError("Event description is required.");
      return;
    }

    const template: MeetingSchedulerEventTemplate = {
      name: trimmedName,
      description: trimmedDescription,
      price: 0,
      earlyBirdPrice: 0,
      earlyBirdDateEnd: formatYesterdayForDateInput(),
      seats: UNLIMITED_SEATS,
      location: null,
      productId: "-",
      currency: "gbp",
      bookingConfirmationEmailSubject: emailSubject.trim() || null,
      bookingConfirmationEmailBodyHtml: emailBody.trim() || null,
      // Listing visibility is derived at run time from scheduler attendees / includes.
      allowedMembershipIds: null,
      memberEventDiscountEligible: false,
      freeForGroups: null,
      bannerImageUrl: existing?.bannerImageUrl || null,
    };

    onSave(template);
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      backdrop="static"
      scrollable
      enforceFocus={false}
      dialogClassName="meeting-scheduler-event-config-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Configure Event (online only)</Modal.Title>
      </Modal.Header>
      <Modal.Body className="meeting-scheduler-event-config-modal__body">
        <Form id="meeting-scheduler-event-config-form" onSubmit={handleSubmit}>
          {formError && (
            <div className="alert alert-danger py-2">{formError}</div>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Booking confirmation email subject</Form.Label>
            <Form.Control
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-0">
            <Form.Label>Booking confirmation email body</Form.Label>
            <Form.Control
              as="textarea"
              rows={6}
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="meeting-scheduler-event-config-form"
          variant="primary"
          disabled={saving}
        >
          {saving ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Saving…
            </>
          ) : (
            "Save event configuration"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
